import {Amplify} from 'aws-amplify';
import {uploadData, getUrl} from 'aws-amplify/storage';
import {store} from '../../redux/store';
import {setUploadingProgress} from '../../redux/actions/user';
import ConfigAWS from '../../amplifyconfiguration.json';

interface ParsedS3Url {
  bucket: string;
  path: string;
  region: string;
}

export class StoryStorageRepository {
  async uploadFile(
    file: File,
    storyId: string,
    type: 'audio' | 'image' | 'video' | 'media',
  ): Promise<string> {
    const fileName = `${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
    const path = `stories/${storyId}/${type}/${fileName}`;
    let bucketName = ConfigAWS.aws_user_files_s3_bucket.replace(
      'uploads',
      'assets',
    );

    try {
      Amplify.configure({
        ...ConfigAWS,
        //@ts-ignore
        aws_user_files_s3_bucket: bucketName,
        Storage: {
          S3: {
            region: 'eu-west-1', // (required) - Amazon S3 bucket region
            bucket: bucketName, // (required) - Amazon S3 bucket URI
          },
        },
      });
      const result = await uploadData({
        path,
        data: file,
        options: {
          onProgress: ({transferredBytes, totalBytes}) => {
            if (totalBytes) {
              const progress = Math.round(
                (transferredBytes / totalBytes) * 100,
              );
              store.dispatch(setUploadingProgress(progress));
            }
          },
        },
      }).result;

      if (result.path.includes('s3.eu-west-1.amazonaws.com')) {
        return result.path;
      } else {
        const baseUrl = 'https://s3.eu-west-1.amazonaws.com';
        return `${baseUrl}/${bucketName}/${result.path}`;
      }
    } catch (error) {
      console.error(`❌ [STORY_STORAGE] Error uploading ${type}:`, error);
      throw error;
    }
  }

  async uploadAudio(file: File, storyId: string): Promise<string> {
    return this.uploadFile(file, storyId, 'audio');
  }

  /**
   * Parses an S3 URL to extract bucket, path, and region.
   * Supports formats:
   * - https://s3.{region}.amazonaws.com/{bucket}/{path}
   * - https://{bucket}.s3.{region}.amazonaws.com/{path}
   */
  private parseS3Url(url: string): ParsedS3Url | null {
    try {
      const urlObj = new URL(url);

      // Format: https://s3.{region}.amazonaws.com/{bucket}/{path}
      const pathStyleMatch = urlObj.hostname.match(
        /^s3\.([a-z0-9-]+)\.amazonaws\.com$/,
      );
      if (pathStyleMatch) {
        const region = pathStyleMatch[1];
        const pathParts = urlObj.pathname.slice(1).split('/');
        const bucket = pathParts[0];
        const path = pathParts.slice(1).join('/');
        return {bucket, path, region};
      }

      // Format: https://{bucket}.s3.{region}.amazonaws.com/{path}
      const virtualHostMatch = urlObj.hostname.match(
        /^(.+)\.s3\.([a-z0-9-]+)\.amazonaws\.com$/,
      );
      if (virtualHostMatch) {
        const bucket = virtualHostMatch[1];
        const region = virtualHostMatch[2];
        const path = urlObj.pathname.slice(1);
        return {bucket, path, region};
      }

      return null;
    } catch {
      return null;
    }
  }

  /**
   * Resolves/signs an S3 URL to get a pre-signed URL with temporary credentials.
   * @param s3Url - The S3 URL to sign (e.g., https://s3.eu-west-1.amazonaws.com/uploads.development.pikado.io/stories/...)
   * @param expiresIn - URL validity in seconds (default: 3600, max: 3600)
   * @returns The signed URL string
   */
  async resolveUrl(s3Url: string, expiresIn: number = 3600): Promise<string> {
    const parsed = this.parseS3Url(s3Url);

    if (!parsed) {
      throw new Error(`Invalid S3 URL format: ${s3Url}`);
    }

    try {
      const decodedPath = decodeURIComponent(parsed.path).replace(/\+/g, ' ');

      const result = await getUrl({
        path: decodedPath,
        options: {
          bucket: {
            bucketName: parsed.bucket,
            region: parsed.region,
          },
          validateObjectExistence: true,
          expiresIn: Math.min(expiresIn, 3600),
        },
      });

      return result.url.toString();
    } catch (error) {
      console.error(`❌ [STORY_STORAGE] Error signing URL:`, error);
      throw error;
    }
  }

  /**
   * Resolves multiple S3 URLs in parallel.
   * @param s3Urls - Array of S3 URLs to sign
   * @param expiresIn - URL validity in seconds (default: 3600)
   * @returns Array of signed URL strings
   */
  async resolveUrls(
    s3Urls: string[],
    expiresIn: number = 3600,
  ): Promise<string[]> {
    return Promise.all(s3Urls.map(url => this.resolveUrl(url, expiresIn)));
  }
}

export const storyStorageRepo = new StoryStorageRepository();
